import { z } from 'zod';
import { zfd } from 'zod-form-data';
import api from '~/utils/api';
import { ClaimType } from '~/utils/constants';
import {
  makeDraftClaimLineItemWithReasonAction,
  makeDraftClaimLineItemWithReasonLoader,
  redirect,
} from '~/utils/routing';
import {
  draftClaimLineItemSchema,
  mediaAssetSchema,
  stringToJSONSchema,
} from '~/utils/schemas';

export const { loader, useLoaderData } = makeDraftClaimLineItemWithReasonLoader(
  ({
    context: {
      draftClaimLineItem,
      settings: { storefrontId, isClaimImageFromCustomerRequired },
      crewClaim: { claimType },
    },
  }) =>
    api
      .calculateAfterReasonSelection({
        params: { storefrontId },
        body: {
          id: draftClaimLineItem.lineItem.id,
          claimType,
          calculatedLineItems: [
            {
              originalStoreOrderLineItem: {
                name: draftClaimLineItem.lineItem.name,
                imgUrl: draftClaimLineItem.lineItem.imgUrl,
                optionsFromPlatform:
                  draftClaimLineItem.lineItem.optionsFromPlatform,
              },
              originalStoreOrderLineItemId: draftClaimLineItem.lineItem.id,
              quantity: draftClaimLineItem.quantity,
              grams: draftClaimLineItem.grams,
              unitPrice: draftClaimLineItem.unitPrice,
              unitTax: draftClaimLineItem.unitTax,
              comments: draftClaimLineItem.comments,
              reasonId: draftClaimLineItem.reasonId,
              reasonCategoryCode: draftClaimLineItem.reasonCategoryCode,
              reasonDetailId: draftClaimLineItem.reasonDetailId,
              advancedExchangeMethod: draftClaimLineItem.advancedExchangeMethod,
            },
          ],
        },
      })
      .then(({ eventCustomFields }) => {
        const requireAssets =
          eventCustomFields?.params.isMediaUploadRequired ??
          (isClaimImageFromCustomerRequired ||
            claimType === ClaimType.warranty);

        // if require assets is true,
        // then the minimumMediaUploadAmount is the provided value or 1
        // if require assets is false, then the minimumMediaUploadAmount is 0
        const requiredMediaCount =
          requireAssets ?
            (eventCustomFields?.params.minimumMediaUploadAmount ?? 1)
          : 0;

        return {
          customFields: eventCustomFields?.params.customFields ?? [],
          mediaUploadInstructions:
            eventCustomFields?.params.mediaUploadInstructions,
          requiredMediaCount,
          showDefaultCommentField:
            eventCustomFields?.params.showDefaultCommentField ?? true,
          claim: draftClaimLineItem,
          isDefaultCommentFieldRequired:
            eventCustomFields?.params.showDefaultCommentField ?
              Boolean(eventCustomFields.params.isDefaultCommentFieldRequired)
            : false,
          uploadFileInfo: {
            storefrontId,
            destination: 'crewClaimImg' as const,
          },
        };
      }),
);

export enum FormKeys {
  Comments = 'comments',
  Assets = 'assets',
  RequiredAssetCount = 'requiredAssetCount',
}

const schema = zfd.formData(
  z
    .object({
      [FormKeys.Comments]: zfd
        .text(z.string().optional())
        .transform((value) => value ?? null),
      [FormKeys.RequiredAssetCount]: zfd.numeric(
        z.number().int().nonnegative(),
      ),
      [FormKeys.Assets]: zfd.repeatableOfType(
        stringToJSONSchema.pipe(mediaAssetSchema),
      ),
    })
    .catchall(draftClaimLineItemSchema.shape.customFields.valueSchema),
);

export const { action, useActionResult } =
  makeDraftClaimLineItemWithReasonAction(({ formData }, appStore) => {
    const results = schema.safeParse(formData);

    if (!results.success) {
      const formInfo = Object.fromEntries([...formData.entries()]) as Record<
        string,
        unknown
      >;

      return new Error('Malformed form data', {
        cause: {
          error: results.error,
          formInfo,
        },
      });
    }

    const {
      [FormKeys.Comments]: comments,
      [FormKeys.Assets]: assets,
      files,
      [FormKeys.RequiredAssetCount]: requiredAssetCount,
      ...customFields
    } = results.data;

    const assetsRequired = requiredAssetCount > 0;

    if (assetsRequired && assets.length < requiredAssetCount) {
      return {
        ok: false,
        message: `A minimum of ${requiredAssetCount} media ${requiredAssetCount === 1 ? 'file is' : 'files are'} required.`,
      };
    }

    appStore.dispatch(
      appStore.actions.draftClaimLineItem.setMeta({
        customFields,
        comments,
        assets,
      }),
    );

    return redirect('../resolution');
  });
